import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PublicHeader } from "@/components/PublicHeader";
import { LineTrend } from "@/components/Charts";
import { AR_SECTOR, arMap } from "@/i18n_halal";
import { ArrowRight, Layers, ShieldCheck, TrendingDown, TrendingUp, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    eyebrow: "Wealth portfolios",
    h1a: "Five portfolios.",
    h1b: "See how they behaved first.",
    sub: "The same ready-made mixes we manage inside the app, with every holding, its weight and its Shariah status — and twelve months of behaviour, rebased to 100, before you open anything.",
    profiles: "Pick a risk level",
    equity: "Equity",
    ret1y: "12-month path",
    dd: "Deepest fall",
    vol: "Volatility",
    expected: "Expected long-run return",
    upDays: "Days finished up",
    bestWorst: "Best / worst day",
    holdings: "What is inside",
    weight: "Weight",
    status: "Shariah",
    purify: "Purify",
    price: "Price",
    cta: "Open your account",
    ctaSub: "Answer eight questions, see your own profile, and fund it when you are ready. No lock-in, no minimum beyond $50.",
    screener: "Screen the holdings yourself",
    loading: "Building the twelve-month paths…",
    compliant: "Shariah compliant",
    window: "12 months to",
  },
  ar: {
    eyebrow: "محافظ الثروة",
    h1a: "خمس محافظ.",
    h1b: "اطّلع على سلوكها أولاً.",
    sub: "التوزيعات الجاهزة نفسها التي نديرها داخل التطبيق، مع كل حصة ووزنها وحكمها الشرعي — واثنا عشر شهراً من السلوك، مُعادة إلى ١٠٠، قبل أن تفتح أي شيء.",
    profiles: "اختر مستوى المخاطرة",
    equity: "الأسهم",
    ret1y: "مسار ١٢ شهراً",
    dd: "أعمق هبوط",
    vol: "التقلب",
    expected: "العائد المتوقع طويل الأجل",
    upDays: "أيام أغلقت صاعدة",
    bestWorst: "أفضل / أسوأ يوم",
    holdings: "ما داخلها",
    weight: "الوزن",
    status: "الحكم",
    purify: "التطهير",
    price: "السعر",
    cta: "افتح حسابك",
    ctaSub: "أجب على ثمانية أسئلة، واطّلع على ملفك، وموّله حين تجهز. لا إلزام، ولا حد أدنى يزيد على ٥٠ دولاراً.",
    screener: "افحص الحصص بنفسك",
    loading: "جارٍ بناء مسارات الاثني عشر شهراً…",
    compliant: "متوافقة مع الشريعة",
    window: "١٢ شهراً حتى",
  },
};

const PROFILE_AR = {
  Conservative: "متحفظة", Moderate: "معتدلة", Balanced: "متوازنة", Growth: "نامية",
  Aggressive: "جسورة",
};

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="rounded-xl bg-secondary/60 p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`numeric text-base font-semibold ${tone}`}>{value}</div>
  </div>
);

export default function Portfolios() {
  const navigate = useNavigate();
  const { lang } = useApp();
  const ar = lang === "ar";
  const c = COPY[ar ? "ar" : "en"];
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("Balanced");

  useEffect(() => {
    axios.get(`${API}/public/portfolios`).then((r) => setD(r.data)).catch(() => {});
  }, []);

  const p = useMemo(
    () => d?.portfolios.find((x) => x.profile === pick) || d?.portfolios[2], [d, pick]);
  const pname = (v) => (ar ? PROFILE_AR[v] || v : v);
  const series = p ? p.series.map((x) => ({ date: x.date, value: x.value })) : [];

  return (
    <div className="min-h-screen bg-background" data-testid="portfolios-page">
      <PublicHeader />

      <div className="navy-strip text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-sheen opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <Layers className="h-3.5 w-3.5" />{c.eyebrow}
          </div>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            data-testid="portfolios-title">
            {c.h1a}<br /><span className="text-white/70">{c.h1b}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">{c.sub}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        {!d ? (
          <p className="text-sm text-muted-foreground">{c.loading}</p>
        ) : (
          <>
            {/* the five */}
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{c.profiles}</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="portfolio-cards">
              {d.portfolios.map((x) => (
                <button key={x.profile} onClick={() => setPick(x.profile)}
                  className={`text-start rounded-2xl border p-5 transition-all ${
                    x.profile === pick ? "border-[#3E2E65] shadow-premium bg-card"
                                       : "border-border bg-card hover:shadow-premium"}`}
                  data-testid={`portfolio-card-${x.profile.toLowerCase()}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold">{pname(x.profile)}</span>
                    <span className="text-[10px] rounded-full bg-secondary px-2 py-0.5 numeric text-muted-foreground">
                      {c.equity} {x.equity_pct}%
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed min-h-[42px]">
                    {x.blurb}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`numeric text-lg font-semibold ${
                      x.stats.return_1y_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {x.stats.return_1y_pct >= 0 ? "+" : ""}{x.stats.return_1y_pct}%
                    </span>
                    <span className="numeric text-[11px] text-rose-600">{x.stats.max_drawdown_pct}%</span>
                  </div>
                </button>
              ))}
            </div>

            {/* the selected one */}
            {p && (
              <div className="mt-6 grid lg:grid-cols-3 gap-5">
                <Card className="lg:col-span-2 p-5 sm:p-6 shadow-premium" data-testid="portfolio-detail">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="text-xl font-bold tracking-tight">{pname(p.profile)}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{p.blurb}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <ShieldCheck className="h-3 w-3" />{c.compliant}
                    </span>
                  </div>
                  <div className="mt-5" data-testid="portfolio-chart">
                    <LineTrend data={series} dataKey="value" height={260} />
                  </div>
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    <Stat label={c.ret1y} testId="stat-return"
                      tone={p.stats.return_1y_pct >= 0 ? "text-emerald-600" : "text-rose-600"}
                      value={`${p.stats.return_1y_pct >= 0 ? "+" : ""}${p.stats.return_1y_pct}%`} />
                    <Stat label={c.dd} value={`${p.stats.max_drawdown_pct}%`} tone="text-rose-600" testId="stat-drawdown" />
                    <Stat label={c.vol} value={`${p.stats.volatility_pct}%`} testId="stat-vol" />
                    <Stat label={c.expected} value={`${p.expected_return_pct}% p.a.`} testId="stat-expected" />
                    <Stat label={c.upDays} value={`${p.stats.positive_days_pct}%`} testId="stat-updays" />
                    <Stat label={c.bestWorst} testId="stat-bestworst"
                      value={`+${p.stats.best_day_pct}% / ${p.stats.worst_day_pct}%`} />
                  </div>
                </Card>

                <Card className="p-5 sm:p-6 shadow-premium" data-testid="portfolio-holdings">
                  <h3 className="text-sm font-semibold">{c.holdings}</h3>
                  <div className="mt-4 space-y-3">
                    {p.holdings.filter((h) => h.weight > 0).map((h) => (
                      <button key={h.symbol}
                        onClick={() => navigate(`/halal-stocks/${encodeURIComponent(h.symbol)}`)}
                        className="w-full text-start group" data-testid={`holding-${h.symbol}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold group-hover:text-[#3E2E65] transition-colors">
                            {h.symbol}
                          </span>
                          <span className="numeric text-sm font-semibold">{h.weight_pct}%</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                          <span className="truncate max-w-[150px]">{h.name}</span>
                          <span className="numeric">{ar ? arMap(AR_SECTOR, h.sector) : h.sector}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-[#3E2E65]" style={{ width: `${h.weight_pct}%` }} />
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Activity className="h-3.5 w-3.5" />{c.window} {d.as_of}
                  </div>
                </Card>
              </div>
            )}

            <p className="mt-6 text-xs text-muted-foreground leading-relaxed">{d.note}</p>

            <div className="mt-12 rounded-3xl navy-strip text-white p-8 sm:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 hero-sheen opacity-50" />
              <div className="relative">
                <p className="text-white/70 max-w-xl mx-auto">{c.ctaSub}</p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Button className="rounded-full px-8 h-11" onClick={() => navigate("/login")}
                    data-testid="portfolios-cta">{c.cta} <ArrowRight className="h-4 w-4 ms-1.5" /></Button>
                  <Button variant="outline" onClick={() => navigate("/top-halal-stocks")}
                    className="rounded-full px-8 h-11 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
                    data-testid="portfolios-screener-cta">{c.screener}</Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
